import { X } from 'lucide-react'
import { AlertDialog, Dialog as DialogPrimitive } from 'radix-ui'
import type { ReactNode, RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../lib/cn'
import { Button } from './button'
import { useReturnFocus } from './focus-return'
import { IconButton } from './icon-button'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

const overlay = 'fixed inset-0 z-40 bg-scrim animate-fade-in'
const panel = 'rounded-panel border border-rule overlay text-ink outline-none animate-float-in'

export interface DialogContentProps {
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  className?: string
  /** Hide the × button (e.g. while a request is running). */
  hideClose?: boolean
  /**
   * The control that takes focus as the dialog opens (default: the first one, often ×). Unlike
   * `autoFocus`, it is focused after the dialog has noted where focus returns on close.
   */
  initialFocus?: RefObject<HTMLElement | null>
  /**
   * Where focus goes when it closes, when it isn't back where it was (the control that opened it was
   * redrawn while it was open): return an element, or null for the default (see ConfirmDialog).
   */
  finalFocus?: () => HTMLElement | null | undefined
}

export function DialogContent({
  title,
  description,
  children,
  footer,
  className,
  hideClose,
  initialFocus,
  finalFocus,
}: DialogContentProps) {
  const { t } = useTranslation()
  const returnFocus = useReturnFocus()
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center p-4">
        <DialogPrimitive.Content
          ref={returnFocus.ref}
          onCloseAutoFocus={closeFocus(finalFocus, returnFocus.onCloseAutoFocus)}
          onOpenAutoFocus={(event) => {
            if (!initialFocus?.current) return
            event.preventDefault()
            initialFocus.current.focus()
          }}
          // The viewport height, less the container's padding: a percentage would resolve against the
          // grid's auto-sized row and never limit a tall dialog, pushing the footer off screen.
          className={cn(
            panel,
            'pointer-events-auto flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col',
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
          // Portalled, yet React events still bubble to whatever opened the dialog (as with Drawer): a
          // dialog's own form opened from inside a page's form must not submit that form or run its
          // Ctrl/⌘+S.
          onSubmit={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (
              (event.ctrlKey || event.metaKey) &&
              (event.key === 'Enter' || event.key.toLowerCase() === 's')
            )
              event.stopPropagation()
          }}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-lg font-semibold text-ink">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-ink-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {hideClose ? null : (
              <DialogPrimitive.Close asChild>
                <IconButton
                  label={t('actions.close')}
                  icon={<X />}
                  size="sm"
                  tooltip={false}
                  className="-me-2"
                />
              </DialogPrimitive.Close>
            )}
          </div>
          {children ? (
            <div className="min-h-0 overflow-auto px-5 py-4">{children}</div>
          ) : (
            <div className="h-4" />
          )}
          {footer ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-rule px-5 py-3">{footer}</div>
          ) : null}
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Portal>
  )
}

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  tone?: 'danger' | 'primary'
  /** While true the confirm button spins and the dialog cannot be dismissed. */
  pending?: boolean
  /** The action cannot be taken now (the dialog says why). */
  confirmDisabled?: boolean
  onConfirm: () => void
  /** Another way out, before Cancel (e.g. "Change date" next to "Post anyway"). */
  extraAction?: ReactNode
  /**
   * Where focus goes when the dialog closes, when it isn't back where it was (the row whose
   * settlement was just removed is gone): return an element, or null for the default.
   */
  finalFocus?: () => HTMLElement | null | undefined
}

/** A dialog's onCloseAutoFocus: `finalFocus` when it names an element, else the usual return. */
function closeFocus(
  finalFocus: (() => HTMLElement | null | undefined) | undefined,
  fallback: (event: Event) => void,
) {
  return (event: Event) => {
    const element = finalFocus?.()
    if (!element?.isConnected) {
      fallback(event)
      return
    }
    event.preventDefault()
    element.focus()
  }
}

/**
 * Asks before an irreversible or disruptive action. The caller closes it (on success) — it does not
 * close itself on confirm, so a failed request keeps the context visible.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  pending = false,
  confirmDisabled = false,
  onConfirm,
  extraAction,
  finalFocus,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const returnFocus = useReturnFocus()
  return (
    <AlertDialog.Root open={open} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={overlay} />
        <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center p-4">
          <AlertDialog.Content
            ref={returnFocus.ref}
            onCloseAutoFocus={closeFocus(finalFocus, returnFocus.onCloseAutoFocus)}
            className={cn(panel, 'pointer-events-auto w-full max-w-sm')}
          >
            <div className="px-5 pt-4 pb-5">
              <AlertDialog.Title className="text-lg font-semibold text-ink">{title}</AlertDialog.Title>
              {description ? (
                <AlertDialog.Description className="mt-1.5 text-base text-ink-muted">
                  {description}
                </AlertDialog.Description>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-rule px-5 py-3">
              {extraAction ? <div className="me-auto flex">{extraAction}</div> : null}
              <AlertDialog.Cancel asChild>
                <Button disabled={pending}>{cancelLabel ?? t('actions.cancel')}</Button>
              </AlertDialog.Cancel>
              <Button variant={tone} loading={pending} disabled={confirmDisabled} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </div>
          </AlertDialog.Content>
        </div>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /**
   * Which edge it slides from. Side sheets for records use the end edge; `bottom` is a phone's sheet
   * (a list's filters), as tall as its content up to most of the screen.
   */
  side?: 'start' | 'end' | 'bottom'
  className?: string
  /** Hide the visible header (e.g. the mobile navigation drawer); the title stays for screen readers. */
  bare?: boolean
  /** A form sheet: the cursor starts in its first field, not on the close button. */
  focusFirstField?: boolean
  /**
   * The control that takes focus as the drawer opens, when it is not a text field (a select that comes
   * first in the form); set by the form as it mounts. Wins over `focusFirstField` when it is set.
   */
  initialFocus?: RefObject<HTMLElement | null>
  /** Where focus goes when it closes, when it isn't back where it was (see ConfirmDialog). */
  finalFocus?: () => HTMLElement | null | undefined
}

/** A side sheet for quick create/edit of small records, and the off-canvas navigation. */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = 'end',
  className,
  bare = false,
  focusFirstField = false,
  initialFocus,
  finalFocus,
}: DrawerProps) {
  const { t } = useTranslation()
  const returnFocus = useReturnFocus()
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={overlay} />
        <DialogPrimitive.Content
          ref={returnFocus.ref}
          onCloseAutoFocus={closeFocus(finalFocus, returnFocus.onCloseAutoFocus)}
          onOpenAutoFocus={
            focusFirstField || initialFocus
              ? (event) => {
                  if (initialFocus?.current) {
                    event.preventDefault()
                    initialFocus.current.focus()
                    return
                  }
                  if (!focusFirstField) return
                  const field = (event.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>(
                    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])',
                  )
                  if (!field) return
                  event.preventDefault()
                  field.focus()
                }
              : undefined
          }
          {...(description && !bare ? {} : { 'aria-describedby': undefined })}
          // Portalled, yet React events still bubble to whatever opened the drawer: one opened from a
          // form (a dropdown's quick create) must not submit that form or run its Ctrl/⌘+S.
          onSubmit={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (
              (event.ctrlKey || event.metaKey) &&
              (event.key === 'Enter' || event.key.toLowerCase() === 's')
            )
              event.stopPropagation()
          }}
          className={cn(
            'fixed z-50 flex w-full flex-col overlay text-ink outline-none',
            side === 'bottom'
              ? 'inset-x-0 bottom-0 max-h-[88dvh] rounded-t-panel border-t border-rule pb-[env(safe-area-inset-bottom)] animate-sheet-up'
              : side === 'end'
                ? 'inset-y-0 end-0 max-w-md border-s border-rule animate-drawer-in'
                : 'inset-y-0 start-0 max-w-72 border-e border-rule animate-drawer-in-start',
            className,
          )}
        >
          {side === 'bottom' ? (
            <span aria-hidden className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-rule-strong" />
          ) : null}
          {bare ? (
            <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          ) : (
            <div className="flex items-start justify-between gap-4 border-b border-rule px-5 py-3.5">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-lg font-semibold">{title}</DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description className="mt-0.5 text-sm text-ink-muted">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              <DialogPrimitive.Close asChild>
                <IconButton
                  label={t('actions.close')}
                  icon={<X />}
                  size="sm"
                  tooltip={false}
                  className="-me-2"
                />
              </DialogPrimitive.Close>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto">{children}</div>
          {footer ? (
            <div className="flex justify-end gap-2 border-t border-rule px-5 py-3">{footer}</div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
